import type { InfiniteData } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import type { PortfolioChatDtoPaginationResultDto } from "@/api/types";
import { type PortfolioChatDto, type PortfolioUserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import type { PortfolioProjectFilterValues } from "@/components/ProjectFilter/PortfolioProjectFilterFields";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { TagSelect } from "@/components/TagSelect/TagSelect";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { parseQueryParamToList } from "@/helpers/query-params";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useBool } from "@/hooks/useBool";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useSignalRHub, useSignalRSubscription } from "@/hooks/useSignalR";
import { usePortfolioChatQueries } from "@/queries/portfolio-chat/queryOptions";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiQueryParams, ApiResponseType } from "@/types/api-types";

import { PortfolioGroupChatDetailsModal } from "../../components/PortfolioGroupChatDetailsModal";
import { PortfolioPrivateChatDetailsModal } from "../../components/PortfolioPrivateChatDetailsModal";
import { PortfolioProjectFilterDrawer } from "../../components/PortfolioProjectFilterDrawer";
import type { PortfolioRouteParams, PortfolioRouteStates } from "../../routes";
import { PortfolioChatListView } from "./components/PortfolioChatListView";
import { CreateChatModal } from "./components/PortfolioCreateChatModal";

export function PortfolioChatListPage(): React.ReactNode {
  const [queryParams, setQueryParams] = useQueryParams<PortfolioRouteParams["chats"]>();
  const [search, setSearch] = useState(() => queryParams.search ?? "");
  const [isOnlyUnreadShown, setIsOnlyUnreadShown] = useState(false);
  const [isPrivateChatDetailsModalOpened, privateChatDetailsModalHandler] = useBool();
  const [isGroupChatDetailsModalOpened, groupChatDetailsModalHandler] = useBool();
  const [isCreateChatModalOpened, createChatModalHandler] = useBool();
  const [isFilterOpened, filtersOpenHandler] = useBool();
  const [userToOpen, setUserIdToOpen] = useState<PortfolioUserDto | undefined>(undefined);
  const [groupChatIdToOpen, setGroupChatIdToOpen] = useState<string | undefined>(undefined);

  const { state: locationState }: { state?: PortfolioRouteStates["chats"] } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const portfolioChatQueries = usePortfolioChatQueries();

  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());

  useEffect(() => {
    setQueryParams({ search: debouncedQuery || undefined }, { replace: true });
  }, [debouncedQuery, setQueryParams]);

  const currFilter = useMemo(
    () => ({
      city: parseQueryParamToList(queryParams.city),
      owner: parseQueryParamToList(queryParams.owner),
      maintenance: parseQueryParamToList(queryParams.maintenance),
    }),
    [queryParams.city, queryParams.owner, queryParams.maintenance],
  );

  const selfAccountQueries = useSelfAccountQueries();
  const portfolioQueries = usePortfolioQueries();
  const { data: linkedProjects = [] } = useQuery(selfAccountQueries.listLinkedProjects());
  const {
    data: portfolioPermissions,
    isPending: isPendingPortfolioPermissions,
    error: portfolioChatPermissionsError,
  } = useQuery(portfolioQueries.permissions());

  const amountActiveFilters = Object.values(currFilter).filter((value) => value && value.length > 0).length;
  const projectIdsToFilter = useMemo(() => {
    const filteredProjectIds = filterProjectsByFilters(linkedProjects, {
      owner: currFilter.owner,
      maintenance: currFilter.maintenance,
      city: currFilter.city,
    }).map((project) => project.id);

    // All projects match the filter, so there's no need to narrow the query down.
    if (linkedProjects.length === filteredProjectIds.length) {
      return undefined;
    }

    // There's limits to the number of projects we can filter. URLs can only be a certain length.
    // We should stay under 2000 characters.
    // 1 projectId is ~15 characters
    // 100 projectIds is ~1500 characters
    // So if its more than a 100 projects we just display all your projects.
    if (filteredProjectIds.length > 100) {
      return undefined;
    }

    return filteredProjectIds;
  }, [linkedProjects, currFilter.owner, currFilter.maintenance, currFilter.city]);

  const query: ApiQueryParams<"getPortfolioChatsV2"> = useMemo(
    () => ({
      ProjectIds: projectIdsToFilter,
      Search: debouncedQuery,
    }),
    [debouncedQuery, projectIdsToFilter],
  );
  const {
    data: chatsData,
    hasNextPage: hasMoreChats,
    refetch: refetchChats,
    fetchNextPage: fetchMoreChats,
    isPending: isPendingChats,
    isFetchingNextPage: isFetchingMoreChats,
    error: chatsError,
  } = useInfiniteQuery(portfolioChatQueries.listInfinite(query));
  const unreadChatsQuery = {
    ...query,
    UnreadOnly: true,
    Limit: 99,
  };
  const {
    data: unreadChats,
    isPending: isPendingUnreadChats,
    refetch: refetchUnreadChats,
  } = useQuery(portfolioChatQueries.list(unreadChatsQuery));

  const onNewChatMessage = useCallback(() => {
    void refetchChats();
    void refetchUnreadChats();
  }, [refetchChats, refetchUnreadChats]);

  const { signalRConnection } = useSignalRHub("global-hub");
  useSignalRSubscription(signalRConnection, SIGNALR_EVENTS.CHAT.NEW_PORTFOLIO_CHAT_MESSAGE_RECEIVED, onNewChatMessage);
  useSignalRSubscription(signalRConnection, SIGNALR_EVENTS.CHAT.NEW_PORTFOLIO_GROUP_CHAT_CREATED, onNewChatMessage);

  const chats = useMemo(() => {
    if (isOnlyUnreadShown) {
      return unreadChats?.items || [];
    } else {
      return chatsData?.pages.flatMap((page) => page.items) ?? [];
    }
  }, [chatsData?.pages, isOnlyUnreadShown, unreadChats?.items]);

  useEffect(() => {
    if (chats.length === 0 || !locationState?.activeChatId || !locationState?.activeChatType) {
      return;
    }

    if (locationState.activeChatType === "private") {
      const privateChatToOpen = chats.find((currChat) => currChat.id === locationState.activeChatId);

      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setUserIdToOpen(privateChatToOpen?.privateChatInfo?.receiver);
      privateChatDetailsModalHandler.setTrue();
    } else {
      setGroupChatIdToOpen(locationState.activeChatId);
      groupChatDetailsModalHandler.setTrue();
    }

    // Clears location state
    void navigate(location.pathname, { replace: true, state: null });
  }, [chats, groupChatDetailsModalHandler, locationState, navigate, privateChatDetailsModalHandler]);

  useEffect(() => {
    chats.forEach((chat) => {
      if (chat.hasUnreadReply) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTFOLIO_CHATS_REPLIES(chat.id),
        });
      }
    });
  }, [chats, queryClient]);

  const optimisticallyUpdateChatUnreadStatusById = (chatId: string) => {
    // All chats
    queryClient.setQueryData<InfiniteData<PortfolioChatDtoPaginationResultDto>>(
      QUERY_KEYS.PORTFOLIO_CHATS_LIST(query),
      (prevData) => {
        if (!prevData) {
          return prevData;
        }

        return {
          ...prevData,
          pages: prevData.pages.map((currPage) => ({
            ...currPage,
            items: currPage.items.map((currChat) => {
              if (currChat.id === chatId) {
                return {
                  ...currChat,
                  hasUnreadReply: false,
                  unreadReplies: 0,
                } satisfies PortfolioChatDto;
              }

              return currChat;
            }),
          })),
        };
      },
    );
    // Unread chats
    queryClient.setQueryData<ApiResponseType<"getPortfolioChatsV2">>(
      QUERY_KEYS.PORTFOLIO_CHATS_LIST(unreadChatsQuery),
      (prevData) => {
        if (!prevData) {
          return prevData;
        }

        const newItems = prevData.data.items.filter((currChat) => {
          return currChat.id !== chatId;
        });

        return {
          ...prevData,
          data: {
            ...prevData.data,
            items: newItems,
            total: newItems.length,
          },
        };
      },
    );
  };

  const handleUpdateProjectFilter = (newFilter: PortfolioProjectFilterValues) => {
    setQueryParams({
      ...queryParams,
      city: newFilter.city?.join(",") || undefined,
      owner: newFilter.owner?.join(",") || undefined,
      maintenance: newFilter.maintenance?.join(",") || undefined,
    });
  };

  const handleCreatePrivateChat = (user: PortfolioUserDto) => {
    createChatModalHandler.setFalse();
    privateChatDetailsModalHandler.setTrue();
    setUserIdToOpen(user);
  };

  const handleOpenChat = (chat: PortfolioChatDto) => {
    if (chat.chatType === "group") {
      setGroupChatIdToOpen(chat.id);
      groupChatDetailsModalHandler.setTrue();
    } else if (chat.privateChatInfo) {
      setUserIdToOpen(chat.privateChatInfo.receiver);
      privateChatDetailsModalHandler.setTrue();
    }

    if (chat.hasUnreadReply) {
      optimisticallyUpdateChatUnreadStatusById(chat.id);
    }
  };

  if (isPendingPortfolioPermissions) {
    return <FullSizeLoader />;
  }

  if (chatsError || portfolioChatPermissionsError) {
    return <ErrorPage error={chatsError || portfolioChatPermissionsError} />;
  }

  const amountUnreadChats = unreadChats?.total ?? 0;
  const isLoadingChats = isPendingChats || isPendingUnreadChats;
  const isLoadingMoreChats = isOnlyUnreadShown ? false : isFetchingMoreChats;
  const hasMoreChatsToLoad = isOnlyUnreadShown ? false : hasMoreChats;
  const handleLoadMoreChats = isOnlyUnreadShown ? undefined : fetchMoreChats;

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.portfolio.chats.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.portfolio.title"),
              },
              {
                name: t("page.portfolio.chats.title"),
              },
            ]}
          />
        }
        actions={
          portfolioPermissions?.canCreateChat ? (
            <Inline gap="sm" align="center">
              <Button data-testid="create-chat" onClick={createChatModalHandler.setTrue}>
                {t("page.chats.new-chat")}
              </Button>
            </Inline>
          ) : null
        }
        header={
          <Inline gap="sm" align="center">
            <SearchInput
              data-testid="search-input"
              defaultValue={search}
              placeholder={t("page.portfolio.chats.search.placeholder")}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              data-testid="portfolio-chats-filter-btn"
              icon={<FilterFunnel01Icon />}
              variant="soft"
              onClick={filtersOpenHandler.toggle}
              aria-pressed={isFilterOpened}
            >
              <Inline as="span" gap="sm" align="center" wrap={false}>
                {t("common.action.filter")}
                {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
              </Inline>
            </Button>
          </Inline>
        }
      >
        <section className="flex flex-1 flex-col gap-space-md">
          <TagSelect
            values={isOnlyUnreadShown ? ["unreadOnly"] : ["all"]}
            items={[
              {
                label: t("common.label.all"),
                value: "all",
              },
              {
                label: t("page.portfolio.chats.filter.unread-only"),
                value: "unreadOnly",
                count: amountUnreadChats,
                "data-testid": "portfolio-chats-filter-option-unread",
              },
            ]}
            onChange={(value) => {
              setIsOnlyUnreadShown(value[0] === "unreadOnly");
            }}
          />
          {isLoadingChats ? (
            <section className="flex flex-1 flex-col justify-center">
              <FullSizeLoader noDelay />
            </section>
          ) : (
            <PortfolioChatListView
              chats={chats}
              isFetchingMoreChats={isLoadingMoreChats}
              hasMoreChats={hasMoreChatsToLoad}
              fetchMoreChats={handleLoadMoreChats}
              onOpenChat={handleOpenChat}
            />
          )}
        </section>
      </DocumentPage>

      <PortfolioProjectFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filtersOpenHandler.set}
        onUpdate={handleUpdateProjectFilter}
        projects={linkedProjects}
        selectedFilter={{
          city: currFilter.city,
          maintenance: currFilter.maintenance,
          owner: currFilter.owner,
        }}
      />
      <CreateChatModal
        isOpened={isCreateChatModalOpened}
        onOpenChange={createChatModalHandler.set}
        onCreatePrivateChat={handleCreatePrivateChat}
      />
      <PortfolioPrivateChatDetailsModal
        isOpened={isPrivateChatDetailsModalOpened && Boolean(userToOpen)}
        onOpenChange={privateChatDetailsModalHandler.set}
        user={userToOpen}
        onExitComplete={() => setUserIdToOpen(undefined)}
      />
      <PortfolioGroupChatDetailsModal
        isOpened={isGroupChatDetailsModalOpened && Boolean(groupChatIdToOpen)}
        onOpenChange={groupChatDetailsModalHandler.set}
        chatId={groupChatIdToOpen}
        onExitComplete={() => setGroupChatIdToOpen(undefined)}
      />
    </>
  );
}
